import { Collapse, styled, useTheme } from '@mui/material';
import { FC, ReactNode, useState } from 'react';
import { Typography } from '../../../../components/Typography/Typography';

type Props = {
  /** Role (or group) name shown in the header, e.g. "Submitter", "Manager". */
  title: string;
  /** One-line summary shown next to the title (typically when collapsed), e.g. "Owns 1 deal: X". */
  summary?: ReactNode;
  /** Whether the section starts expanded. Ignored when `collapsible` is false. */
  defaultOpen?: boolean;
  /** Keep the section open regardless of user toggling (used to reveal fields with validation errors). */
  forceOpen?: boolean;
  /** When false, renders a static, non-expandable header (e.g. roles with no extra settings). */
  collapsible?: boolean;
  children?: ReactNode;
};

/**
 * Collapsible panel grouping one role's configuration pickers under a header (role name + summary).
 * Design refresh of the old always-open `RoleFieldsSection`; it only changes presentation (expand /
 * collapse), never which fields render or how they behave.
 */
export const CollapsibleRoleSection: FC<Props> = ({
  title,
  summary,
  defaultOpen = false,
  forceOpen = false,
  collapsible = true,
  children,
}) => {
  const { colors } = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  const expanded = collapsible ? open || forceOpen : false;

  return (
    <Section>
      <Header
        type="button"
        $clickable={collapsible}
        onClick={collapsible ? () => setOpen((prev) => !prev) : undefined}
        aria-expanded={collapsible ? expanded : undefined}
      >
        <HeaderText>
          <Typography variant="subtitle2" color={colors.text.main}>
            {title}
          </Typography>
          {summary && (
            <Typography variant="caption" color={colors.text.secondary}>
              {summary}
            </Typography>
          )}
        </HeaderText>
        {collapsible && (
          <Chevron
            $expanded={expanded}
            viewBox="0 0 24 24"
            style={{ color: colors.icon.default }}
            aria-hidden
          >
            <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </Chevron>
        )}
      </Header>
      {collapsible && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Body>{children}</Body>
        </Collapse>
      )}
    </Section>
  );
};

const Section = styled('div')`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.surfaceBackground.bg4};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  overflow: hidden;
`;

const Header = styled('button')<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 14px 16px;
  background: transparent;
  border: none;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const HeaderText = styled('div')`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;
  min-width: 0;
`;

const Chevron = styled('svg')<{ $expanded: boolean }>`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  transition: transform 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
`;

const Body = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 16px;
`;
